import { useEffect, useRef, useState } from "react";
import { Send, Paperclip, X, ArrowLeft, Plus, MessageSquare, FileText, ShieldCheck, UserRound, Stethoscope } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useAuth } from "@/context/AuthContext";
import { useMessaging } from "@/context/MessagingContext";
import { useKeyNav } from "@/lib/useKeyNav";
import { StatusBadge } from "@/components/portal/AdminTicketInbox";

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function RoleIcon({ role, size = 16 }) {
  if (role === "admin") return <ShieldCheck size={size} />;
  if (role === "doctor") return <Stethoscope size={size} />;
  return <UserRound size={size} />;
}

/**
 * Full messaging inbox for patient / doctor / admin portals.
 * Left: conversation list. Right: active thread + composer. Plus a compose modal.
 */
export default function Inbox({ role, initialConversationId = null, accent = "#185FA5", onClose }) {
  const { user } = useAuth();
  const { tick, refreshSummary } = useMessaging();
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(initialConversationId);
  const [meta, setMeta] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [compose, setCompose] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [composeKind, setComposeKind] = useState(null); // admin: 'patient' | 'doctor'
  const [composeValue, setComposeValue] = useState("");
  const [adminTickets, setAdminTickets] = useState(null); // patient/doctor: ticket chooser list (null=not in step)
  const [newSubject, setNewSubject] = useState("");
  const [adminSubject, setAdminSubject] = useState(""); // admin compose: ticket subject
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const composeKey = useKeyNav(searchResults, (r) => startWith(r.id), { onEscape: () => { setComposeValue(""); setSearchResults([]); } });
  const logRef = useRef(null);

  const scrollDown = () => setTimeout(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, 60);

  const loadConvs = async () => {
    try { const { data } = await api.get("/conversations"); setConvs(data.conversations || []); }
    catch { /* ignore */ }
  };

  const loadThread = async (id) => {
    if (!id) { setMeta(null); setMsgs([]); return; }
    try {
      const { data } = await api.get(`/conversations/${id}/messages`);
      setMeta({ name: data.counterpart_name, role: data.counterpart_role,
                ticket_code: data.conversation?.ticket_code, subject: data.conversation?.subject,
                status: data.conversation?.status, id_label: data.conversation?.counterpart_id_label,
                kind: data.conversation?.kind });
      setMsgs(data.messages || []);
      scrollDown();
      refreshSummary();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { loadConvs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadThread(activeId); }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps
  // realtime: refresh list + open thread on any inbound event
  useEffect(() => { loadConvs(); if (activeId) loadThread(activeId); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    const t = text.trim();
    if ((!t && files.length === 0) || !activeId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", t);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/conversations/${activeId}/messages`, fd);
      setMsgs((prev) => [...prev, data.message]);
      setText(""); setFiles([]);
      scrollDown();
      loadConvs();
      loadThread(activeId);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openCompose = async () => {
    setCompose(true);
    setComposeKind(null);
    setComposeValue("");
    setSearchResults([]);
    setAdminTickets(null);
    setNewSubject("");
    setAdminSubject("");
    if (role !== "admin") {
      try { const { data } = await api.get("/messaging/contacts"); setContacts(data.contacts || []); }
      catch { setContacts([]); }
    }
  };

  // Admin: live search patients by name/Patient ID, or doctors by name/NMC.
  useEffect(() => {
    if (role !== "admin" || !compose || !composeKind) return;
    const q = composeValue.trim();
    if (!q) { setSearchResults([]); return; }
    let cancelled = false;
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/messaging/search", { params: { kind: composeKind, q } });
        if (!cancelled) setSearchResults(data.results || []);
      } catch { if (!cancelled) setSearchResults([]); }
      finally { if (!cancelled) setSearchLoading(false); }
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [composeValue, composeKind, compose, role]);

  const startWith = async (target, extra = {}) => {
    try {
      const { data } = await api.post("/conversations/start", { target, ...extra });
      setCompose(false);
      setSearchResults([]);
      setComposeValue("");
      setComposeKind(null);
      setAdminTickets(null);
      setNewSubject("");
      setAdminSubject("");
      await loadConvs();
      setActiveId(data.conversation.id);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  // Patient/Doctor: opening the support (admin) contact reveals a ticket chooser.
  const openAdminTickets = async () => {
    setAdminTickets([]);
    try { const { data } = await api.get("/my-tickets"); setAdminTickets(data.tickets || []); }
    catch { setAdminTickets([]); }
  };

  // Support: hand a ticket to admin / mark it resolved (both remove it from the inbox).
  const escalateTicket = async () => {
    if (!activeId) return;
    try {
      await api.post(`/support/tickets/${activeId}/escalate`, {});
      toast("Escalated to admin");
      setActiveId(null); await loadConvs(); refreshSummary();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  const resolveTicket = async () => {
    if (!activeId) return;
    try {
      await api.post(`/support/tickets/${activeId}/resolve`);
      toast("Marked resolved");
      setActiveId(null); await loadConvs(); refreshSummary();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  const addFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length) setFiles((prev) => [...prev, ...list]);
    e.target.value = "";
  };

  return (
    <div className={`inbox ${activeId ? "has-active" : ""}`} style={{ "--ib-accent": accent }} data-testid="inbox">
      {/* conversation list */}
      <aside className="inbox-list" data-testid="inbox-list">
        <div className="inbox-list-head">
          {onClose && <button className="ib-iconbtn" onClick={onClose} data-testid="inbox-close" title="Back"><ArrowLeft size={16} /></button>}
          <h3>Inbox</h3>
          <button className="ib-new" onClick={openCompose} data-testid="inbox-compose"><Plus size={15} /> New</button>
        </div>
        <div className="inbox-rows">
          {convs.length === 0 && <p className="ib-empty"><MessageSquare size={26} /><br />No conversations yet.<br /><span>Start one with “New”.</span></p>}
          {convs.map((c) => (
            <button
              key={c.id}
              className={`inbox-row ${activeId === c.id ? "on" : ""} ${c.unread > 0 ? "unread" : ""}`}
              onClick={() => setActiveId(c.id)}
              data-testid={`inbox-conv-${c.id}`}
            >
              <span className={`ib-avatar ${c.counterpart_role}`}><RoleIcon role={c.counterpart_role} /></span>
              <span className="ib-row-main">
                <span className="ib-row-top"><b>{c.counterpart_name}</b><time>{fmtTime(c.last_at).split(",").slice(-1)}</time></span>
                {c.ticket_code && <span className="ib-row-ticket"><span className="tkt-code">{c.ticket_code}</span><StatusBadge status={c.status} />{c.counterpart_id_label ? ` ${c.counterpart_id_label}` : ""}{c.subject ? ` ${c.subject}` : ""}</span>}
                <span className="ib-row-prev">{c.last_text || "No messages yet"}</span>
              </span>
              {c.unread > 0 && <span className="ib-unread" data-testid={`inbox-unread-${c.id}`}>{c.unread}</span>}
            </button>
          ))}
        </div>
      </aside>

      {/* thread */}
      <section className="inbox-thread" data-testid="inbox-thread">
        {!activeId ? (
          <div className="ib-placeholder"><MessageSquare size={40} /><p>Select a conversation to read and reply.</p></div>
        ) : (
          <>
            <div className="ib-thread-head">
              <button className="ib-iconbtn only-mobile" onClick={() => setActiveId(null)} data-testid="inbox-back"><ArrowLeft size={16} /></button>
              <span className={`ib-avatar ${meta?.role}`}><RoleIcon role={meta?.role} /></span>
              <div className="ib-thread-name">
                <b data-testid="inbox-thread-name">{meta?.name || "…"}</b>
                {meta?.ticket_code
                  ? <span className="ib-ticket-chip"><span className="tkt-code">{meta.ticket_code}</span><StatusBadge status={meta.status} />{meta.id_label ? ` · ${meta.id_label}` : ""}{meta.subject ? ` · ${meta.subject}` : ""}</span>
                  : <span className="ib-role-chip">{meta?.role}</span>}
              </div>
              {role === "support" && meta?.ticket_code && meta?.status !== "resolved" && meta?.status !== "escalated" && (
                <div className="ib-thread-actions">
                  <button className="ib-act-btn resolve" onClick={resolveTicket} data-testid="inbox-resolve">Resolve</button>
                  <button className="ib-act-btn escalate" onClick={escalateTicket} data-testid="inbox-escalate">Escalate to admin</button>
                </div>
              )}
            </div>

            <div className="ib-log" ref={logRef} data-testid="inbox-messages">
              {msgs.length === 0 && <p className="ib-log-empty">No messages yet. Say hello 👋</p>}
              {msgs.map((m) => {
                const me = m.sender_id === user.id;
                return (
                  <div className={`ib-bubble ${me ? "me" : "them"}`} key={m.id}>
                    {!me && <span className="ib-who">{m.sender_name}</span>}
                    {m.text && <span className="ib-text">{m.text}</span>}
                    {(m.files || []).map((f, i) => (
                      <a key={i} className="ib-file" href={`${API}/files/${f.file_id}`} target="_blank" rel="noreferrer" data-testid={`inbox-file-${f.file_id}`}>
                        <FileText size={14} /> {f.filename}
                      </a>
                    ))}
                    <span className="ib-time">{fmtTime(m.created_at)}</span>
                  </div>
                );
              })}
            </div>

            {files.length > 0 && (
              <div className="ib-attachbar" data-testid="inbox-attachments">
                {files.map((f, i) => (
                  <span className="ib-chip" key={i}><FileText size={12} /> {f.name}
                    <button onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}

            <div className="ib-composer">
              <label className="ib-iconbtn" title="Attach files" data-testid="inbox-attach">
                <Paperclip size={18} />
                <input type="file" hidden multiple onChange={addFiles} />
              </label>
              <input
                className="ib-input"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
                placeholder="Type a message…"
                data-testid="inbox-input"
              />
              <button className="ib-send" onClick={send} disabled={busy} data-testid="inbox-send"><Send size={16} /></button>
            </div>
          </>
        )}
      </section>

      {/* compose modal */}
      {compose && (
        <div className="modal-bg" onClick={() => setCompose(false)}>
          <div className="modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()} data-testid="compose-modal">
            <button className="m-close" onClick={() => setCompose(false)}>✕</button>
            <h3 className="h" style={{ fontSize: 20, marginTop: 0 }}>New message</h3>

            {role === "admin" ? (
              <>
                {!composeKind ? (
                  <>
                    <p className="lead" style={{ marginBottom: 14 }}>Who would you like to message?</p>
                    <div className="compose-choice">
                      <button className="compose-pick patient" onClick={() => { setComposeKind("patient"); setComposeValue(""); setSearchResults([]); }} data-testid="compose-pick-patient">
                        <span className="ib-avatar patient"><UserRound size={18} /></span>
                        <span><b>Patient</b><small>Search by name, Patient ID, mobile or email</small></span>
                      </button>
                      <button className="compose-pick doctor" onClick={() => { setComposeKind("doctor"); setComposeValue(""); setSearchResults([]); }} data-testid="compose-pick-doctor">
                        <span className="ib-avatar doctor"><Stethoscope size={18} /></span>
                        <span><b>Doctor</b><small>Search by name, NMC number, mobile or email</small></span>
                      </button>
                      <button className="compose-pick support" onClick={() => { setComposeKind("support"); setComposeValue(""); setSearchResults([]); }} data-testid="compose-pick-support">
                        <span className="ib-avatar admin"><ShieldCheck size={18} /></span>
                        <span><b>Customer Support</b><small>Search by name, employee ID, mobile or email</small></span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <button className="compose-backlink" onClick={() => { setComposeKind(null); setComposeValue(""); setSearchResults([]); }} data-testid="compose-back">
                      <ArrowLeft size={14} /> Choose someone else
                    </button>
                    <p className="lead" style={{ margin: "10px 0 12px" }}>
                      {composeKind === "patient"
                        ? "Search by patient name, Patient ID, mobile number or email."
                        : composeKind === "support"
                          ? "Search by support employee name, employee ID, mobile or email."
                          : "Search by doctor name, NMC / state-council registration number, mobile or email."}
                    </p>
                    <label className="ib-field-label">
                      {composeKind === "patient" ? "Name · Patient ID · Mobile · Email"
                        : composeKind === "support" ? "Name · Employee ID · Mobile · Email"
                          : "Name · NMC · Mobile · Email"}
                    </label>
                    <input
                      className="ib-lookup-input"
                      value={composeValue}
                      onChange={(e) => setComposeValue(e.target.value)}
                      onKeyDown={composeKey.onKeyDown}
                      role="combobox"
                      aria-expanded={searchResults.length > 0}
                      aria-activedescendant={composeKey.activeIndex >= 0 && searchResults[composeKey.activeIndex] ? `compose-result-${searchResults[composeKey.activeIndex].id}` : undefined}
                      placeholder={composeKind === "patient"
                        ? "e.g. Priya · 2nd-0000-0000-0001 · 98765 43210 · priya@demo.com"
                        : "e.g. Dr. Ramesh · KMC 119333 · 98765 43210 · doctor@clinic.in"}
                      autoFocus
                      data-testid="compose-lookup-input"
                    />

                    <div className="compose-results" data-testid="compose-results" ref={composeKey.listRef} role="listbox">
                      {composeValue.trim() === "" && (
                        <p className="compose-results-empty">Start typing to search…</p>
                      )}
                      {composeValue.trim() !== "" && searchLoading && (
                        <p className="compose-results-empty">Searching…</p>
                      )}
                      {composeValue.trim() !== "" && !searchLoading && searchResults.length === 0 && (
                        <p className="compose-results-empty">
                          No {composeKind === "patient" ? "patients" : composeKind === "support" ? "support employees" : "doctors"} found.
                        </p>
                      )}
                      {searchResults.map((r, i) => {
                        const idLine = composeKind === "patient"
                          ? (r.patient_code || "—")
                          : composeKind === "support"
                            ? (r.employee_no || "—")
                            : `${r.reg_no || "—"}${r.specialty ? " · " + r.specialty : ""}`;
                        const contact = [r.phone, r.email].filter(Boolean).join(" · ");
                        return (
                          <button
                            key={r.id}
                            type="button"
                            id={`compose-result-${r.id}`}
                            role="option"
                            aria-selected={i === composeKey.activeIndex}
                            className={`compose-result-row ${i === composeKey.activeIndex ? "active" : ""}`}
                            onMouseEnter={() => composeKey.setActiveIndex(i)}
                            onClick={() => startWith(r.id)}
                            data-testid={`compose-result-${r.id}`}
                          >
                            <span className={`ib-avatar ${composeKind === "support" ? "admin" : composeKind}`}>
                              {composeKind === "patient" ? <UserRound size={16} /> : composeKind === "support" ? <ShieldCheck size={16} /> : <Stethoscope size={16} />}
                            </span>
                            <span className="compose-result-main">
                              <b>{r.name}</b>
                              <small>{idLine}</small>
                              {contact && <small className="compose-result-contact">{contact}</small>}
                            </span>
                          </button>
                        );
                      })}
                      {searchResults.length > 0 && <div className="biz-suggest-hint">↑ ↓ to move · ↵ to open · esc to clear</div>}
                    </div>
                  </>
                )}
              </>
            ) : (
              <>
                {adminTickets !== null ? (
                  <>
                    <button className="compose-backlink" onClick={() => setAdminTickets(null)} data-testid="ticket-chooser-back">
                      <ArrowLeft size={14} /> Choose someone else
                    </button>
                    <p className="lead" style={{ margin: "10px 0 12px" }}>
                      Continue an existing conversation, or raise a brand-new ticket for a different query.
                    </p>

                    <div className="newticket-box" data-testid="new-ticket-box">
                      <label className="ib-field-label">Raise a new ticket</label>
                      <input
                        className="ib-lookup-input"
                        value={newSubject}
                        onChange={(e) => setNewSubject(e.target.value)}
                        placeholder="Briefly describe your query (subject)…"
                        data-testid="new-ticket-subject"
                      />
                      <button
                        className="btn btn-ink full"
                        style={{ marginTop: 8 }}
                        onClick={() => startWith("admin", { subject: newSubject })}
                        data-testid="new-ticket-create"
                      >
                        <Plus size={15} /> Start new ticket
                      </button>
                    </div>

                    <div className="ticket-chooser-list" data-testid="my-tickets-list">
                      <label className="ib-field-label" style={{ marginTop: 6 }}>Your existing tickets</label>
                      {adminTickets.length === 0 && <p className="compose-results-empty">No previous tickets — start a new one above.</p>}
                      {adminTickets.map((t) => (
                        <button
                          key={t.id}
                          className={`ticket-pick ${t.unread > 0 ? "unread" : ""}`}
                          onClick={() => startWith("admin", { ticket_id: t.id })}
                          data-testid={`my-ticket-${t.id}`}
                        >
                          <span className="ib-avatar admin"><ShieldCheck size={16} /></span>
                          <span className="ticket-pick-main">
                            <span className="ticket-pick-top"><span className="tkt-code">{t.ticket_code}</span><StatusBadge status={t.status} /><b>{t.subject}</b></span>
                            <small>{t.last_text || "No messages yet"}</small>
                          </span>
                          {t.unread > 0 && <span className="ib-unread">{t.unread}</span>}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="lead" style={{ marginBottom: 14 }}>Choose who you’d like to message.</p>
                    <div className="ib-contacts">
                      {contacts.length === 0 && <p className="lead">No contacts available yet.</p>}
                      {contacts.map((c) => (
                        <button
                          key={c.id}
                          className="ib-contact"
                          onClick={() => (c.role === "admin" && role !== "support" ? openAdminTickets() : startWith(c.id))}
                          data-testid={`contact-${c.id}`}
                        >
                          <span className={`ib-avatar ${c.role}`}><RoleIcon role={c.role} /></span>
                          <span className="ib-contact-main"><b>{c.name}</b><small>{c.role === "admin" ? "Support team · tickets" : c.role}</small></span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
